import { useRouter, Href } from 'expo-router';
import { cssInterop } from 'nativewind';
import { StyleProp, ViewStyle } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { Heading } from './heading';
import { Button, ButtonGroup, ButtonText } from './ui/button';
import { Card } from './ui/card';
import { Text } from './ui/text';
import { View } from './ui/view';
import { VStack } from './ui/vstack';

import { REFERRAL_COPY } from '@/constants/referrals';
import { ROUTES } from '@/constants/routes';
import {
  useCurrentPagePath,
  buildLoginPathWithRedirect,
  buildSignupPathWithRedirect,
} from '@/hooks/useRedirectPath';
import { trackCreateAccountCtaTriggered } from '@/util/analyticsEvents';

cssInterop(AuthCard, {
  style: 'className',
});

export type AuthCardProps = {
  style?: StyleProp<ViewStyle>;
  className?: string;
  title?: string;
  text?: string;
};

export function AuthCard({ style, className = '', title, text }: AuthCardProps) {
  const router = useRouter();

  const currentPath = useCurrentPagePath();

  return (
    <View testID="auth-card">
      <Card
        size="md"
        className={twJoin(
          'w-full flex-row flex-wrap items-center justify-between gap-7',
          className
        )}
        style={style}
        variant="filled">
        <VStack space="sm" className="items-start justify-center">
          <Heading size="h4-md">{title ?? 'Not on Primum?'}</Heading>

          <Text>{text ?? REFERRAL_COPY.referrer.authCardDefaultCopy}</Text>
        </VStack>

        <ButtonGroup className="flex-shrink flex-row flex-wrap overflow-auto">
          <Button
            size="sm"
            onPress={() => {
              const loginPath = currentPath
                ? buildLoginPathWithRedirect(currentPath)
                : ROUTES.LOGIN;

              router.push(loginPath as Href);
            }}
            className="flex-row">
            <ButtonText>Log in</ButtonText>
          </Button>

          <Button
            size="sm"
            onPress={() => {
              trackCreateAccountCtaTriggered({ cta: 'sign_up', source: 'auth_card' });

              const signupPath = currentPath
                ? buildSignupPathWithRedirect(currentPath)
                : ROUTES.CREATE_ACCOUNT;

              router.navigate(signupPath as Href);
            }}
            className="flex-row">
            <ButtonText>Create account</ButtonText>
          </Button>
        </ButtonGroup>
      </Card>
    </View>
  );
}
